import { FC, useState } from 'react';
import { Button } from '@/components';
import { Preference } from './types';
import { TypeCommunicationPreferenceFields } from '@/core/generated-types/TypeCommunicationPreference';
import { editUser } from '@/lib/api/accountManager/apis/editUser';
import {
  TypeUserPreferenceName,
  TypeUserPreferenceRecord,
} from '@/lib/api/accountManager/types';
import { Alert } from '@/components/ui/Alert';
import Checkbox from '@/components/subflow-v3/components/Checkbox/Checkbox';
import PhoneNumberInput from './PhoneNumberInput';
import useLocale from '@/hooks/useLocale';
import { formatPhoneNumber } from 'react-phone-number-input';

interface CommunicationPreferenceCardProps
  extends TypeCommunicationPreferenceFields {
  userUuid: string;
  preferences: { [key: string]: Preference };
  userPhoneNumber?: string | null;
  updateSavedPreferences: () => void;
  isChanged: boolean;
  onCheckboxChange: (key: string) => void;
  onPhoneNumberChange: (value: string) => void;
  marketingLabel: string;
  transactionalLabel: string;
  phoneNumberLabel: string;
  invalidPhoneNumberMessage: string;
  ctaText: string;
}

const CommunicationPreferenceCard: FC<CommunicationPreferenceCardProps> = ({
  userUuid,
  preferences,
  userPhoneNumber,
  onPhoneNumberChange,
  updateSavedPreferences,
  isChanged,
  onCheckboxChange,
  marketingLabel,
  transactionalLabel,
  phoneNumberLabel,
  invalidPhoneNumberMessage,
  ctaText,
  title,
  description,
  successMessage,
  preferenceTypes,
}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumberValidationError, setPhoneNumberValidationError] =
    useState<boolean>(false);
  const locale = useLocale();

  const getLabel = (preferenceType: string) => {
    if (preferenceType.includes('marketing')) {
      return marketingLabel;
    }
    if (preferenceType.includes('transactional')) {
      return transactionalLabel;
    }
    return preferenceType;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const userPreferences = preferenceTypes.map((type) => {
      const { value } = preferences[type];
      const userPreferenceObj = {
        preference_name: type as TypeUserPreferenceName,
        preference_value: value,
      };
      return userPreferenceObj;
    });

    try {
      const response = await editUser({
        userUuid,
        userData: {
          user_preferences: userPreferences as TypeUserPreferenceRecord[],
          phone_number: userPhoneNumber,
        },
      });

      if (response.ok) {
        setIsSuccess(true);
        updateSavedPreferences();
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (preferenceType: string) => {
    setIsSuccess(false);
    onCheckboxChange(preferenceType);
  };

  const handleInputChange = (value: string) => {
    onPhoneNumberChange(value);
    setIsSuccess(false);
  };

  return (
    <div className="bg-indigo-pastel mb-4 rounded py-10 px-8 w-full md:w-[500px]">
      <h3 className="font-bold text-h2 mb-1">{title}</h3>
      <p className="text-base mb-4">{description}</p>
      <ul className="text-base font-bold mb-6 flex flex-col space-y-2 md:space-x-12 md:flex-row md:space-y-0">
        {preferenceTypes.map((preferenceType) => (
          <li key={preferenceType}>
            <Checkbox
              id={preferenceType}
              onChange={() => handleCheckboxChange(preferenceType)}
              value={preferences[preferenceType]?.value ?? false}
              label={getLabel(preferenceType)}
              className="flex items-center"
              checkboxLabelClass="text-base font-bold !relative cursor-pointer pointer-events-auto"
            />
          </li>
        ))}
      </ul>
      <div className="my-6">
        <p className="text-base text-charcoal font-bold mb-1">
          {phoneNumberLabel}
        </p>
        {!isSuccess && (
          <PhoneNumberInput
            value={userPhoneNumber || ''}
            onChange={handleInputChange}
            errorMessage={invalidPhoneNumberMessage}
            onValidationError={() => setPhoneNumberValidationError(true)}
            onClearError={() => setPhoneNumberValidationError(false)}
            locale={locale}
          />
        )}
        {isSuccess && userPhoneNumber && (
          <p>{formatPhoneNumber(userPhoneNumber)}</p>
        )}
      </div>

      {isSuccess && (
        <div className="my-4">
          <Alert message={successMessage} />
        </div>
      )}
      <Button
        type="button"
        ctaText={ctaText}
        onClick={handleSubmit}
        fullWidth
        disabled={isSubmitting || !isChanged || phoneNumberValidationError}
        includeSpinner={isSubmitting}
      />
    </div>
  );
};

export default CommunicationPreferenceCard;
